import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/system';
import AddIcon from '@mui/icons-material/AddRounded';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import ContactChip from '@pages/ContactsPage/ContactChip';
import Autocomplete from '@components/Autocomplete';
import { ModalBase } from '@components/Modal';
import { Typography } from '@components/Typography';
import { useTranslation, useThrottleValue } from '@hooks';
import { getUserContacts } from '@helpers/userName';
import { handleApiError } from '@helpers/api';
import { contactsActions, reviewActions } from '@actions';
import { reviewsService } from '@services';

const StyledModal = styled(ModalBase)({
    '& .modal-base-paper': {
        maxWidth: 480,
        width: 'calc(100% - 32px)',
    },
    '& .modal-base-content': {
        padding: '24px',
        height: 'auto',
        minHeight: '100px',
    },
});

const AssignContact = ({
    contactId,
    contactName,
    contactPhoneNumber,
    contactEmail,
    contactExternalId,
    reviewId,
    onClickContactDetails,
}) => {
    const [search, setSearch] = useState({ open: false, query: '', value: '' });
    const [selectedContact, setSelectedContact] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { t } = useTranslation();

    const { searchedContacts } = useSelector(state => state.contacts);
    const reviews = useSelector(state => state.reviews.reviews || []);
    const dispatch = useDispatch();

    const throttledSearch = useThrottleValue(search.query, 1000);

    useEffect(() => {
        if (!search.open) {
            return;
        }
        if (throttledSearch.length === 0) {
            dispatch(contactsActions.resetSearchedContacts());
            return;
        }
        if (throttledSearch.length >= 2) {
            dispatch(contactsActions.getSearchedContacts({ searchQuery: throttledSearch }, 0, 10));
        }
    }, [throttledSearch, search.open]);

    const onSelectContact = items => {
        setSelectedContact(items.length ? items[0] : null);
        setSearch(prev => ({ ...prev, value: items.length ? String(items[0].id) : '' }));
    };

    const onAssign = async () => {
        if (!selectedContact) return;
        setIsSubmitting(true);
        try {
            const updatedReview = await reviewsService.assignContact({ contactId: selectedContact.id }, reviewId);
            const reviewsCopy = reviews.slice();
            const reviewIndex = reviewsCopy.findIndex(r => r.reviewId === reviewId);
            if (reviewIndex !== -1 && updatedReview) {
                reviewsCopy[reviewIndex] = updatedReview;
                dispatch(reviewActions.updateReviews(reviewsCopy));
            }
            handleCloseModal();
        } catch (error) {
            handleApiError(error);
        }
        setIsSubmitting(false);
    };

    const onUnassign = async () => {
        setIsSubmitting(true);
        try {
            const updatedReview = await reviewsService.unassignContact(reviewId);
            const reviewsCopy = reviews.slice();
            const reviewIndex = reviewsCopy.findIndex(r => r.reviewId === reviewId);
            if (reviewIndex !== -1 && updatedReview) {
                reviewsCopy[reviewIndex] = updatedReview;
                dispatch(reviewActions.updateReviews(reviewsCopy));
            }
            setSearch({ open: false, query: '', value: '' });
        } catch (error) {
            handleApiError(error);
        }
        setIsSubmitting(false);
    };

    const handleCloseModal = () => {
        setSearch({ open: false, query: '', value: '' });
        setSelectedContact(null);
        dispatch(contactsActions.resetSearchedContacts());
    };

    const [firstName, lastName] = (contactName || '').split(' ');

    if (contactId) {
        return (
            <Typography
                variant="caption"
                sx={{
                    cursor: 'pointer',
                    color: 'var(--primary-main)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                }}
            >
                <PersonOutlineRoundedIcon sx={{ fontSize: 'inherit' }} />
                <span onClick={() => onClickContactDetails(contactId, 'view')}>
                    {getUserContacts(
                        {
                            firstName,
                            lastName,
                            phoneNumber: contactPhoneNumber,
                            email: contactEmail,
                            externalId: contactExternalId,
                        },
                        '-',
                    )}
                </span>
                <CloseIcon
                    sx={{
                        fontSize: 'inherit',
                        color: 'var(--text-tertiary)',
                        cursor: isSubmitting ? 'default' : 'pointer',
                        '&:hover': { color: 'var(--text-secondary)' },
                    }}
                    onClick={!isSubmitting ? onUnassign : undefined}
                />
            </Typography>
        );
    }

    return (
        <>
            <Typography
                variant="caption"
                sx={{
                    cursor: isSubmitting ? 'default' : 'pointer',
                    color: 'var(--primary-main)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    opacity: isSubmitting ? 0.5 : 1,
                }}
                onClick={!isSubmitting ? () => setSearch(prev => ({ ...prev, open: true })) : undefined}
            >
                <AddIcon sx={{ fontSize: 'inherit' }} />
                {t('ReviewsManage.assignContact')}
            </Typography>
            <StyledModal
                isOpen={search.open}
                handleClose={handleCloseModal}
                title={t('ReviewsManage.assignContact')}
                onPrimaryAction={onAssign}
                primaryActionText={t('ReviewsManage.assignContact')}
                primaryActionDisabled={!selectedContact || isSubmitting}
            >
                <Autocomplete
                    label={t('Listings.contact')}
                    options={searchedContacts}
                    ValueComponent={ContactChip}
                    OptionComponent={ContactChip}
                    disabled={isSubmitting}
                    inputProps={{
                        value: search.query,
                        onChange: e => setSearch(prev => ({ ...prev, query: e.target.value })),
                        style: { display: search.value.length > 0 ? 'none' : 'block' },
                    }}
                    onChangeAutoComplete={onSelectContact}
                    sx={{ marginBottom: 0, width: '100%' }}
                />
            </StyledModal>
        </>
    );
};

export default AssignContact;
